import { ScrollArea, Stack } from "@mantine/core"
import { Button, Card } from "@tfg/ui"
import { useEffect, useMemo, useState, useRef } from "react"
import { useTranslation } from "react-i18next"

import {
  FindMappingByTypeResponseDto,
  findMappingByTypeResponseDtoType,
  FindMappingByTypeResponseDtoType,
} from "~/gen/api/types"
import { EditableSelect } from "~/pages/Brand/OrganizationSettings/Mapping/components/EditableSelect.tsx"
import { MappingSelect } from "~/pages/Brand/OrganizationSettings/Mapping/components/MappingSelect.tsx"
import {
  TypeKeys,
  TypeMapping,
} from "~/pages/Brand/OrganizationSettings/Mapping/types.ts"

type Props = {
  typesList: TypeKeys[]
  types: FindMappingByTypeResponseDto[]
  typeName: FindMappingByTypeResponseDtoType
}

export type typeComponent = {
  isNew: boolean
  type: TypeMapping
}

export function MappingTable({ typesList, types, typeName }: Props) {
  const { t } = useTranslation("organizationSettings")

  const typeComponents = useMemo(() => {
    return types
      .sort((a, b) => (a.typeId === null ? -1 : b.typeId === null ? 1 : 0))
      .map((type) => {
        return {
          isNew: false,
          type,
        }
      })
  }, [types])

  const [typeComponent, setTypeComponent] =
    useState<typeComponent[]>(typeComponents)

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTypeComponent(typeComponents)
  }, [typeComponents])

  const translation =
    typeName === "PRODUCT"
      ? t("mapping.product")
      : typeName == "MATERIAL"
        ? t("mapping.material")
        : t("mapping.standards")

  const handleCreate = () => {
    if (typeComponent.filter((component) => component.isNew).length > 0) return
    const type = {
      isNew: true,
      type: {
        alias: "",
        id: "0",
        type: findMappingByTypeResponseDtoType.PRODUCT,
        typeId: 0,
        key: "",
      },
    }
    setTypeComponent([...typeComponent, type])

    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" })
    }, 0)
  }

  const handleCancel = (id: string) => {
    const newTypes = typeComponent.filter((type) => type.type.id !== id)
    setTypeComponent(newTypes)
  }

  return (
    <>
      <Card>
        <Card.Header
          title={`${translation} ${t("mapping.mapping")}`}
          mainCta={
            <Button onClick={handleCreate}>{t("mapping.create")}</Button>
          }
          divider={true}
        />

        <Card.Section>
          <ScrollArea mah="50vh" style={{ overflowY: "scroll" }}>
            {typeComponent.map((type, index) => {
              return (
                <Stack
                  key={type.type.id}
                  ref={
                    index === typeComponent.length - 1 ? scrollRef : undefined
                  }
                  px="var(--tfg-organism-padding-x)"
                  py="var(--tfg-organism-padding-y)"
                >
                  {type.isNew ? (
                    <EditableSelect
                      typesList={typesList}
                      mappingType={type}
                      typeName={typeName}
                      handleCancel={() => handleCancel(type.type.id)}
                    />
                  ) : (
                    <MappingSelect
                      typesList={typesList}
                      mappingType={type}
                      typeName={typeName}
                    />
                  )}
                </Stack>
              )
            })}
          </ScrollArea>
        </Card.Section>
      </Card>
    </>
  )
}
